import { useSelector } from 'react-redux';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HomeIcon from '@mui/icons-material/Home';
import WidgetsIcon from '@mui/icons-material/Widgets';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import QuoteIcon from '@mui/icons-material/AssignmentTurnedIn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import GroupIcon from '@mui/icons-material/Group';
import SignalCellularAlt2BarIcon from '@mui/icons-material/SignalCellularAlt2Bar';
import { AppState } from '../Common/Reducer/rootReducerTypes';
import { BackendTypeName } from '../Common/Utils/backendDataParser';
import useBackendData from '../hooks/useBackendData';
import { ReactComponent as ActivityIcon } from '../../src/components/ActivitySettings/show_chart.svg';

// components
import { NavItemProps } from 'reactstrap';
import useLocales from '../hooks/useLocales';
import { IdentityPermission, OrderPermission, QuotePermission, InstallationPermission, InvoicePermission, DiscrepancyPermission } from '../Common/enums';
import { getPermissions, shouldPermissionExist } from '../utils/userInfoKeyValuePairChecker';
// ----------------------------------------------------------------------

export default function SidebarConfig() {
  const { translate } = useLocales();
  const [selectedClient] = useBackendData();
  const userInformation = useSelector((state: AppState) => state.userInfo.userInformation);
  const identityPermissions: Array<IdentityPermission> = userInformation.userPermissions['identityPermissions'];
  const quotePermissions = getPermissions(userInformation.userPermissions, 'quotePermissions');
  const installationPermissions: Array<InstallationPermission> = userInformation.userPermissions['sitePermissions']; //This is still called 'sitePermissions' in the userInformation endpoint, but it is actually installation permission
  const orderPermissions: Array<OrderPermission> = userInformation.userPermissions['orderPermissions'];
  const invoicePermissions = getPermissions(userInformation.userPermissions, 'invoicePermissions');
  const userInfo = useSelector((state: AppState) => state.userInfo.userInformation);
  const shouldActivityPermissionsExist = shouldPermissionExist(userInfo.userPermissions, 'activitySettingsPermissions');
  const discrepancyPermissions = getPermissions(userInformation.userPermissions, 'discrepancyPermissions');

  const createBaseMenuItems = (): Array<NavItemProps> => {
    let items = new Array<NavItemProps>();

    items.push({ title: translate('sidebar.Start'), path: '/', icon: <HomeIcon />, index: 0 });

    if (orderPermissions?.includes(OrderPermission.view_orders) === true) {
      items.push({ title: translate('portalFrame.Order list'), path: '/orderlist', icon: <AssignmentIcon />, index: 1 });
    }

    if (quotePermissions?.includes(QuotePermission.view_quotes) === true) {
      items.push({ title: translate('portalFrame.Quote list'), path: '/quotelist', icon: <QuoteIcon />, index: 2 });
    }

    if (invoicePermissions?.includes(InvoicePermission.view_invoices) === true) {
      items.push({ title: translate('portalFrame.Invoice list'), path: '/invoicelist', icon: <ReceiptIcon />, index: 4 });
    }

    if (installationPermissions?.includes(InstallationPermission.view_installations) === true) {
      items.push({ title: translate('sidebar.Assets'), path: '/assetlist', icon: <WidgetsIcon />, index: 3 })
    }

    if (discrepancyPermissions?.includes(DiscrepancyPermission.view_discrepancy) === true) {
      items.push({ title: translate('portalFrame.DiscrepancyList'), path: '/discrepancyList', icon: <SignalCellularAlt2BarIcon />, index: 5 });
    }

    return items;
  }

  const adminMenuItems = userInformation.isCustomerPortalSuperAdmin ?
    [
      { title: translate('portalFrame.Companies'), path: '/companies', icon: <BusinessIcon />, index: 4 },
      { title: translate('portalFrame.Permissions'), path: '/permissions', icon: <SettingsIcon />, index: 5 },
      shouldActivityPermissionsExist && { title: translate('portalFrame.Activity'), path: '/activity', icon: <ActivityIcon />, index: 7 }
    ].filter(Boolean) : // Remove falsy values
    [
      { title: translate('portalFrame.Users'), path: '/users', icon: <GroupIcon />, index: 6 },
    ];

  const sidebarConfig = [
    {
      subheader: '',
      items: createBaseMenuItems()
    },
    ...selectedClient && selectedClient.backendName === BackendTypeName.Handyman &&
      (userInformation.isCustomerPortalSuperAdmin || identityPermissions?.includes(IdentityPermission.view_identities) === true) ?
      [{
        subheader: translate('portalFrame.Admin'),
        items: adminMenuItems
      }] :
      []
  ];
  
  return sidebarConfig;
}
